<!--
  PC 顶栏（设计基准宽度 1920）
  - 左侧站点 Logo，右侧主导航 + 外链图标
  - 样式走 postcss-pxtorem（px→rem），与 rem-pc 根字号联动
-->
<template>
  <header class="pc-nav">
    <div class="pc-nav-inner">
      <button
        type="button"
        class="pc-nav-logo"
        @click="onLogoClick"
      >
        <img
          src="@/assets/image/logo/logo.webp"
          :alt="headerImgAlt.siteLogo"
          class="pc-nav-logo-img"
          width="138"
          height="47"
          decoding="async"
          fetchpriority="high"
        />
      </button>

      <nav
        class="pc-nav-links"
        :aria-label="pcHeaderCopy.primaryNavAriaLabel"
      >
        <span
          v-for="(item, index) in displayNavItems"
          :key="item.name"
          class="pc-nav-item-wrap"
        >
          <span
            v-if="index > 0"
            class="pc-nav-slash"
            aria-hidden="true"
          >/</span>
          <router-link
            v-if="item.routePath"
            :to="item.routePath"
            tag="button"
            type="button"
            class="pc-nav-item"
            exact
            active-class="pc-nav-item--active"
            :aria-current="$route.path === item.routePath ? 'page' : undefined"
          >
            {{ item.label }}
          </router-link>
          <button
            v-else
            type="button"
            class="pc-nav-item"
            :class="{ 'pc-nav-item--active': activeNavName === item.name }"
            :aria-current="activeNavName === item.name ? 'true' : undefined"
            @click="onNavItemClick(item)"
          >
            {{ item.label }}
          </button>
        </span>

        <span class="pc-nav-slash" aria-hidden="true">/</span>

        <span class="pc-nav-soc-wrap">
          <a
            :href="pcHeaderLinks.youtube"
            class="pc-nav-icon-wrap"
            target="_blank"
            rel="noopener noreferrer"
          >
            <img
              src="@/assets/image/logo/youTube.webp"
              :alt="headerImgAlt.youtube"
              class="pc-nav-icon"
              width="31"
              height="21"
              decoding="async"
            />
          </a>
        </span>
        <span class="pc-nav-slash" aria-hidden="true">/</span>
        <span class="pc-nav-soc-wrap">
          <a
            :href="pcHeaderLinks.twitterIntent"
            class="pc-nav-icon-wrap"
            target="_blank"
            rel="noopener noreferrer"
            :aria-label="pcHeaderCopy.twitterShareAriaLabel"
          >
            <img
              src="@/assets/image/logo/X.webp"
              alt=""
              class="pc-nav-icon pc-nav-icon--x"
              width="21"
              height="21"
              decoding="async"
              aria-hidden="true"
            />
          </a>
        </span>
      </nav>
    </div>
  </header>
</template>

<script>
import indexContent from '@/views/data/index-content.json'
import contentVisibility from '@/views/data/content-visibility.json'

const PC_HEADER = indexContent.pcHeader

/**
 * PC 顶栏：无 routePath → 首页 / 内锚点（name 须与 PcIndex 内 NAV_NAME_SCROLL 等一致）
 * 有 routePath → 子页路由；文案与导航维护 index-content.json → pcHeader
 */
export default {
  name: 'PcHeader',
  data() {
    return {
      // ========== 运营 / 页面配置（优先维护）==========
      pcHeaderCopy: PC_HEADER.pcHeaderCopy,
      pcHeaderLinks: PC_HEADER.pcHeaderLinks,
      navItems: PC_HEADER.navItems,
      pcHeaderRouteToActiveName: PC_HEADER.pcHeaderRouteToActiveName,
      headerImgAlt: PC_HEADER.imgAlt,

      // ========== 运行时状态（一般无需改）==========
      /** 当前高亮 tab（首页锚点或子页 route name） */
      activeNavName: 'top',
    }
  },
  watch: {
    $route(to, from) {
      this.syncActiveNavFromRoute(to.path, from && from.path)
    }
  },
  mounted() {
    this.syncActiveNavFromRoute(this.$route.path)
    this.$root.$on('pc-index-nav-scroll-spy', this.onIndexNavScrollSpy)
  },
  beforeDestroy() {
    this.$root.$off('pc-index-nav-scroll-spy', this.onIndexNavScrollSpy)
  },
  computed: {
    /** 见 content-visibility.json → header（与 MobileNav 共用） */
    displayNavItems() {
      const h = contentVisibility.header ?? {}
      const showStreaming = h.showStreamingNav !== false
      const showGoods = h.showGoodsNav !== false
      return this.navItems.filter((item) => {
        if (!showStreaming && item.name === 'streaming') return false
        if (!showGoods && item.name === 'goods') return false
        return true
      })
    }
  },
  methods: {
    /** 首页滚动时同步 TOP/NEWS/ABOUT/DJ 高亮（由 PcIndex 发出） */
    onIndexNavScrollSpy(name) {
      if (this.$route.path !== '/') return
      const anchorNames = this.displayNavItems
        .filter((item) => !item.routePath)
        .map((item) => item.name)
      if (!anchorNames.includes(name)) return
      this.activeNavName = name
    },
    /**
     * 子页高亮对应 tab；从子页回到 /（如浏览器返回）时锚点高亮回到 TOP
     */
    syncActiveNavFromRoute(path, fromPath) {
      const map = this.pcHeaderRouteToActiveName
      if (map[path]) {
        this.activeNavName = map[path]
        return
      }
      if (path === '/' && fromPath && map[fromPath]) {
        this.activeNavName = 'top'
      }
    },
    requestScrollTo(navName) {
      this.$root.$emit('pc-nav-scroll-to', navName)
    },
    goHomeAndScroll(navName) {
      this.$root.__pcPendingNavScrollName = navName
      this.$router.push('/').catch(() => {}).then(() => {
        this.$nextTick(() => {
          requestAnimationFrame(() => {
            this.requestScrollTo(navName)
          })
        })
      })
    },
    onLogoClick() {
      this.activeNavName = 'top'
      if (this.$route.path !== '/') {
        this.goHomeAndScroll('top')
      } else {
        this.requestScrollTo('top')
      }
    },
    onNavItemClick(item) {
      this.activeNavName = item.name
      if (this.$route.path !== '/') {
        this.goHomeAndScroll(item.name)
      } else {
        this.requestScrollTo(item.name)
      }
    },
  }
}
</script>

<style lang="scss" scoped>
/* 设计稿 1920，postcss-pxtorem 将本文件中的 px 转为 rem */

.pc-nav {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 99;
  height: 56px;
  background: rgba(255, 255, 255, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    box-sizing: border-box;
    vertical-align: middle;
  }

  &-inner {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    padding: 0 110px 0 120px;
    box-sizing: border-box;
    gap: 16px;
  }

  &-logo {
    flex-shrink: 0;
    display: block;
    width: 137.84px;
    height: 46.54px;
    min-width: 137.84px;
    min-height: 46.54px;
    margin: 0;
    padding: 0;
    border: none;
    background: transparent;
    font: inherit;
    line-height: 0;
    cursor: pointer;
    appearance: none;
    -webkit-tap-highlight-color: transparent;
    text-align: center;
    border-radius: 4px;

    &:focus-visible {
      outline: 2px solid #37fff1;
      outline-offset: 2px;
    }

    &-img {
      display: block;
      width: 100%;
      height: 100%;
      object-fit: contain;
    }
  }

  &-links {
    display: flex;
    align-items: center;
    gap: 0;
    flex-wrap: nowrap;
    flex: 1;
    min-width: 0;
    justify-content: flex-end;
    /* 仅链接区横向滚动，主 Logo 固定左侧 */
    overflow-x: auto;
    overflow-y: hidden;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
    -ms-overflow-style: none;

    &::-webkit-scrollbar {
      display: none;
      width: 0;
      height: 0;
    }
  }

  /* v-for 分组用：子级直接参与 .pc-nav-links 的 flex，视觉上无多余包裹 */
  &-item-wrap,
  &-soc-wrap {
    display: contents;
  }

  &-slash {
    flex-shrink: 0;
    font-size: 16px;
    font-weight: 700;
    color: #191616;
    margin: 0 2px;
    user-select: none;
  }

  &-item {
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 13px 14px;
    padding: 4px 10px;
    font-size: 16px;
    font-weight: 700;
    font-family: inherit;
    color: #191616;
    text-decoration: none;
    border: none;
    background: transparent;
    cursor: pointer;
    appearance: none;
    -webkit-tap-highlight-color: transparent;
    border-radius: 4px;
    white-space: nowrap;
    box-sizing: border-box;
    flex-shrink: 0;
    transform-origin: center center;
    transition:
      transform 0.24s cubic-bezier(0.34, 1.45, 0.64, 1),
      background-color 0.2s ease,
      box-shadow 0.24s ease,
      filter 0.2s ease;

    &:hover {
      background-color: #fff380;
      transform: translateY(-2px) scale(1.04);
      box-shadow: 0 6px 14px rgba(0, 0, 0, 0.16);
      filter: brightness(1.03);
    }

    &:active {
      background-color: #fff380;
      transform: translateY(0) scale(0.96);
      box-shadow: 0 2px 6px rgba(0, 0, 0, 0.12);
      filter: brightness(0.97);
      transition-duration: 0.08s;
    }

    &:focus-visible {
      outline: 2px solid #37fff1;
      outline-offset: 2px;
    }

    &--active {
      background-color: #fff380;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

      &:hover {
        transform: translateY(-2px) scale(1.04);
        box-shadow: 0 6px 14px rgba(0, 0, 0, 0.18);
        filter: brightness(1.03);
      }

      &:active {
        transform: translateY(0) scale(0.96);
        box-shadow: 0 2px 6px rgba(0, 0, 0, 0.12);
        filter: brightness(0.97);
        transition-duration: 0.08s;
      }
    }
  }

  &-icon-wrap {
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 20px;
    flex-shrink: 0;
    border-radius: 4px;
    transform-origin: center center;
    transition: transform 0.2s cubic-bezier(0.34, 1.4, 0.64, 1),
      filter 0.15s ease;
    text-decoration: none;
    color: inherit;

    &:hover {
      transform: scale(1.06);
    }

    &:active {
      transform: scale(0.88);
      filter: brightness(0.85);
      transition-duration: 0.06s;
    }

    &:focus-visible {
      outline: 2px solid #37fff1;
      outline-offset: 3px;
    }
  }

  &-icon {
    display: block;
    width: 31px;
    height: 21px;
    flex-shrink: 0;
    object-fit: contain;
    pointer-events: none;

    &--x {
      width: 21px;
      height: 21px;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    &-item {
      transition: none !important;

      &:hover,
      &:active,
      &--active:hover,
      &--active:active {
        transform: none !important;
        filter: none !important;
      }

      &:hover,
      &--active:hover {
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08) !important;
      }
    }

    &-icon-wrap {
      transition: none !important;

      &:hover,
      &:active {
        transform: none !important;
        filter: none !important;
      }
    }
  }
}
</style>
